import React, { useState, useRef, useEffect } from "react";

// List of commands for the header bar
const commandsList = [
  "help", "about", "skills", "experience",
  "contact", "education", "certifications", "clear"
];

// Map of commands and their outputs
const commands = {
  help: `Available commands:
about          - Learn about me
skills         - See my technical skills
experience     - My work experience
contact        - How to reach me
education      - My educational background
certifications - View my certifications
clear          - Clear the terminal`,

  about: `👋 Hello, I'm Akshay!

As a Senior Software Engineer with over 7 years of experience in frontend development, I have a strong foundation in modern web development technologies and a track record of delivering high-quality, user-friendly software.

I am highly skilled in HTML, CSS, and Typescript/JavaScript, and have a deep understanding of frameworks such as React and Angular. I have a passion for creating intuitive and visually appealing user interfaces, and I am always seeking ways to improve the user experience.

In my previous roles as Software Engineer, I have successfully led development teams, mentored junior engineers, and collaborated with cross-functional teams to deliver complex projects on time. `,
  skills: `🍳 Technical Skills:
Languages: JavaScript, Typescript
Frontend: React, Next.js, Redux, Tailwind CSS, Material UI
Backend: Node.js, Express.js, MongoDB, PostgreSQL
Tools: Git, Docker, Jenkins, Webpack
`,
  experience: `💼  Professional Experience:

🔹 Software Engineer — Lloyds Technology Centre India
📍 Hyderabad, India | 🗓️ Jun 2025 – Present
• Working on scalable backend services using Express.js, Node.js, and related technologies.

🔹 Senior Software Engineer — Innovatech Technology Solutions
📍 Abu Dhabi, UAE | 🗓️ Jul 2022 – Jun 2025
• Built and maintained user-facing applications using React & Angular.
• Led frontend teams and mentored junior developers.
• Collaborated with designers, backend engineers & PMs.
• Participated in code reviews, agile planning, and estimation.
• Actively improved UI/UX and implemented best practices.

🔹 Software Engineer — Innovatech Technology Solutions
📍 Hyderabad, India | 🗓️ Jul 2020 – Jun 2022
• Developed performant and modular frontend solutions.
• Used Angular, React, Next.js, and Node.js.
• Built cross-browser, mobile-friendly UIs.
• Solved business problems through collaborative solutions.

🔹 Full Stack Developer — Keyvertex Technologies
📍 Hyderabad, India | 🗓️ May 2019 – Jun 2020
• Full-stack development with Angular, Node.js, MongoDB, PostgreSQL.
• Built scalable web/mobile apps from scratch.
• Debugged issues, optimized modules, maintained production apps.
• Aligned with corporate quality standards and strategy.

🔹 Web Developer — Bhavans College
📍 Hyderabad Area, India | 🗓️ May 2018 – Apr 2019
• Built a brand-new responsive website for the college.
• Maintained organization’s internal database systems.`,

  contact: `📫 Email: akshayiscoding@icloud.com | GitHub: akshayiscoding`,
  education: `🎓 Education:

Master’s in Computer Science  
Bharathiya Vidya Bhavans Vivekananda College of Science, Humanities & Commerce  
(2016 – 2018  )

Bachelor’s in Computer Science  
Bharathiya Vidya Bhavans Vivekananda College of Science, Humanities & Commerce  
(2013 – 2016 ) 
`,

  certifications: `📜 Certifications:
Frontend Developer (React): [View Certificate](https://www.hackerrank.com/certificates/5f112df7aeec)
React (Basic): [View Certificate](https://www.hackerrank.com/certificates/a0e40b60b04c)
JavaScript (Basic): [View Certificate](https://www.hackerrank.com/certificates/7215add1640a)
Angular (Intermediate): [View Certificate](https://www.hackerrank.com/certificates/240c385cb72b)
`,

  sudo: "🛑 Permission denied: You are not root.",
};

const OutputLine = ({ text, isError }) => {
  const [displayedText, setDisplayedText] = useState("");
  const containerRef = useRef(null);
  const indexRef = useRef(0);

  useEffect(() => {
    let timeoutId;

    const typeCharacter = () => {
      if (!text || typeof text !== "string" || isError) {
        setDisplayedText(text ?? "");
        return;
      }

      if (indexRef.current < text.length) {
        setDisplayedText((prev) => prev + text.charAt(indexRef.current));
        indexRef.current += 1;
        timeoutId = setTimeout(typeCharacter, 15);
      }
    };

    typeCharacter();

    return () => {
      clearTimeout(timeoutId);
      indexRef.current = 0;
    };
  }, [text, isError]);

  // Convert [text](url) to clickable JSX
  const parseLinks = (input) => {
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(input)) !== null) {
      parts.push(input.slice(lastIndex, match.index));
      parts.push(
        <a
          key={match[2]}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#00ffff", textDecoration: "underline" }}
        >
          {match[1]}
        </a>
      );
      lastIndex = regex.lastIndex;
    }

    parts.push(input.slice(lastIndex));
    return parts;
  };

  return (
    <pre className={isError ? "error-output" : "output"}>
      {parseLinks(displayedText)}
    </pre>
  );
};

function Terminal() {
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [history, setHistory] = useState([
    { type: "input", text: "welcome" },
    {
      type: "output",
      text: `Hi, I am Akshay, a Software Engineer.\n\nWelcome to my interactive portfolio terminal!\nType 'help' to see available commands.`,
    },
  ]);
  // const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const terminalRef = useRef(null);

  const handleCommand = (command) => {
    if (command === "clear") {
      setHistory([]);
      return;
    }

    const output =
      typeof commands[command] === "string"
        ? commands[command]
        : `Command not found: ${command}`;

    setHistory((prev) => [
      ...prev,
      { type: "input", text: command },
      { type: "output", text: output },
    ]);

    setCommandHistory((prev) => [...prev, command]);
    setHistoryIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const trimmed = input.trim();
      if (trimmed !== "") {
        handleCommand(trimmed);
        setInput("");
      }
    } else if (e.key === "ArrowUp") {
      if (commandHistory.length === 0) return;

      const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(historyIndex - 1, 0);
      setInput(commandHistory[newIndex]);
      setHistoryIndex(newIndex);
    } else if (e.key === "ArrowDown") {
      if (commandHistory.length === 0) return;

      const newIndex = Math.min(historyIndex + 1, commandHistory.length - 1);
      if (newIndex >= commandHistory.length - 1) {
        setInput("");
        setHistoryIndex(-1);
      } else {
        setInput(commandHistory[newIndex]);
        setHistoryIndex(newIndex);
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      const matches = Object.keys(commands).filter((cmd) =>
        cmd.startsWith(input)
      );
      if (matches.length === 1) setInput(matches[0]);
    }
  };

  useEffect(() => {
    terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [history]);

  return (
    <div className="terminal-wrapper">
      <div className="header-bar">
        {commandsList.map((cmd, index) => (
          <>
            <span
              key={index}
              className="header-link"
            >
              {cmd}
            </span>
          </>
        ))}
      </div>
      <div className="divider">

      </div>
      <div className="terminal" ref={terminalRef}>
        {history.map((item, index) => {
          if (item.type === "input") {
            return (
              <div key={item.id} className="command-line">
                <span className="user">akshayiscoding@portfolio:~$</span>{" "}
                <span className="typed">{item?.text}</span>
              </div>
            );
          } else {
            const isError = item.text?.startsWith("Command not found");
            return (
              <OutputLine key={index} text={item.text} isError={isError} />
            );
          }
        })}
        <div className="input-line">
          <span className="user">akshayiscoding@portfolio:~$</span>
          <input
            autoFocus
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="input"
          />
        </div>
      </div>
      <div className="footer">{new Date().toLocaleString()}</div>
    </div>
  );
}

export default Terminal;
